<script setup lang="ts">
import { type Node, sortLinks } from './util'

const props = defineProps<{
  node: Node
}>()

const isPage = computed(() => props.node._id !== undefined && !props.node._id.endsWith('_dir.yml'))

const dm = useDM()

const open = ref(false)

const children = computed(() => {
  if (!props.node.children) return []
  if (props.node.isListing) return []

  const children: Node[] = sortLinks(props.node.children, dm.value)

  return children
})
</script>

<template>
  <li v-if="isPage || children.length" class="node" :class="{ open }">
    <NuxtLink v-if="isPage" :to="node._path" no-prefetch class="node-link">
      <Icon v-if="node.icon" :name="node.icon" class="node-icon" />
      <span>
        {{ node.title }}
      </span>
      <Icon v-if="children.length" name="estia:triquetra" class="chevron" @click.prevent="open = !open" />
    </NuxtLink>
    <span v-else class="node-label" @click="open = !open">
      <Icon v-if="node.icon" :name="node.icon" class="node-icon" />
      <span>
        {{ node.title }}
      </span>
      <Icon v-if="children.length" name="estia:triquetra" class="chevron" />
    </span>

    <ul v-if="children.length" class="node-list">
      <NavigationLink v-for="child in children" :key="child._id" :node="child" />
    </ul>
  </li>
</template>

<style scoped lang="postcss">
.node {
  display: flex;
  align-items: center;
  position: relative;

  .node-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5em;
  }

  .node-link {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    padding: 0.25em;
    flex: 1;
    gap: 0.5em;

    > span {
      flex: 1;
      font-family: var(--font-stack-title);
      font-variation-settings: 'wdth' 5;
    }

    &.router-link-active {
      color: var(--secondary);

      .icon {
        color: var(--text-highlight);
      }
    }
  }

  .node-label {
    display: flex;
    align-items: center;
    padding: 0.25em;
    flex: 1;
    gap: 0.33rem;

    > span {
      text-transform: uppercase;
      letter-spacing: 1px;
      font-weight: 500;
      font-size: 0.75em;
      flex: 1;
    }
  }

  .icon {
    color: var(--text);
    width: 1.25rem;
    height: 1.25rem;

    &.chevron {
      width: 1rem;
      height: 1rem;
    }
  }

  @media (width < 950px) {
    .node-link,
    .node-label {
      min-height: 40px;
    }
  }
}

.chevron {
  margin: 0;
}
</style>
